import { usePermissions } from './usePermissions';
import { useProxy } from '../contexts/ProxyContext';

export const useEffectivePermissions = () => {
  const { hasRole: originalHasRole, roles: originalRoles } = usePermissions();
  const { effectiveRole, isProxyActive } = useProxy();

  // When in proxy mode, simulate the permissions of the proxy role
  const hasRole = (roleName: string): boolean => {
    if (isProxyActive) {
      // In proxy mode, check if the effective role matches or has higher privileges
      const roleHierarchy = ['client', 'user', 'manager', 'admin', 'super'];
      const effectiveIndex = roleHierarchy.indexOf(effectiveRole);
      const requestedIndex = roleHierarchy.indexOf(roleName);
      
      // If requesting a role that's lower or equal to effective role, allow it
      return effectiveIndex >= requestedIndex;
    }
    
    // Not in proxy mode, use original permissions
    return originalHasRole(roleName);
  };

  const hasPermission = (resource: string, action: string): boolean => {
    if (isProxyActive) {
      // In proxy mode, simulate permissions based on effective role
      switch (effectiveRole) {
        case 'super':
          return true; // Super admin has all permissions
        case 'admin':
          return !resource.includes('system') || action === 'read'; // Admin can read system data but not modify
        case 'manager':
          return ['clients', 'campaigns', 'artists', 'releases'].includes(resource);
        case 'user':
          return ['artists', 'releases'].includes(resource) && action === 'read';
        case 'client':
          return resource === 'clients' && action === 'read';
        default:
          return false;
      }
    }
    
    // Not in proxy mode, use original permissions (you'd need to implement this)
    return true; // For now, return true - you can implement this based on your permission system
  };

  return {
    hasRole,
    hasPermission,
    effectiveRole,
    isProxyActive,
    roles: isProxyActive ? [{ name: effectiveRole, description: '' }] : (originalRoles || []),
  };
};
